import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { faqApi } from 'api/faqApi';

export const saveFaq = createAsyncThunk(
  'faq/saveFaq',
  async (faqData, { rejectWithValue }) => {
    try {
      const response = await faqApi.saveFaq(faqData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to save FAQ');
    }
  }
);

export const assignFaqToPages = createAsyncThunk(
  'faq/assignFaqToPages',
  async ({ faqId, pageAssignments }, { rejectWithValue }) => {
    try {
      const response = await faqApi.assignFaqToPages(faqId, pageAssignments);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to assign FAQ to pages');
    }
  }
);

export const fetchAllFaqs = createAsyncThunk(
  'faq/fetchAllFaqs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await faqApi.getAllFaqs();
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch FAQs');
    }
  }
);

export const fetchFaqAssignments = createAsyncThunk(
  'faq/fetchFaqAssignments',
  async (faqId, { rejectWithValue }) => {
    try {
      const response = await faqApi.getFaqAssignments(faqId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch FAQ assignments');
    }
  }
);

export const deleteFaq = createAsyncThunk(
  'faq/deleteFaq',
  async (faqId, { rejectWithValue }) => {
    try {
      await faqApi.deleteFaq(faqId);
      return faqId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete FAQ');
    }
  }
);

const initialState = {
  allFaqsList: [],
  faqAssignments: [],
  loading: false,        // For GET operations
  submitting: false,     // For POST/PUT operations (save FAQ)
  assigning: false,      // For assignment operations
  error: null
};

const faqSlice = createSlice({
  name: 'faq',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearFaqAssignments: (state) => {
      state.faqAssignments = [];
    }
  },
  extraReducers: (builder) => {
    builder
      // Save FAQ - uses submitting
      .addCase(saveFaq.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(saveFaq.fulfilled, (state, action) => {
        state.submitting = false;
        // Update or add FAQ in the list
        const existingIndex = state.allFaqsList.findIndex(f => f.id === action.payload.id);
        if (existingIndex !== -1) {
          state.allFaqsList[existingIndex] = action.payload;
        } else {
          state.allFaqsList.unshift(action.payload);
        }
      })
      .addCase(saveFaq.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })

      // Assign FAQ to Pages - uses assigning
      .addCase(assignFaqToPages.pending, (state) => {
        state.assigning = true;
        state.error = null;
      })
      .addCase(assignFaqToPages.fulfilled, (state, action) => {
        state.assigning = false;
        // Update FAQ in the list
        const existingIndex = state.allFaqsList.findIndex(f => f.id === action.payload.id);
        if (existingIndex !== -1) {
          state.allFaqsList[existingIndex] = action.payload;
        }
      })
      .addCase(assignFaqToPages.rejected, (state, action) => {
        state.assigning = false;
        state.error = action.payload;
      })

      // Fetch All FAQs
      .addCase(fetchAllFaqs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllFaqs.fulfilled, (state, action) => {
        state.loading = false;
        
        // Backend returns { faqs: [...], pagination: {...} }
        // So we need to extract the faqs array
        if (action.payload?.faqs && Array.isArray(action.payload.faqs)) {
          state.allFaqsList = action.payload.faqs;
        } else if (Array.isArray(action.payload)) {
          state.allFaqsList = action.payload;
        } else {
          state.allFaqsList = [];
        }
      })
      .addCase(fetchAllFaqs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.allFaqsList = [];
      })

      // Fetch FAQ Assignments - uses loading
      .addCase(fetchFaqAssignments.pending, (state) => {
        // state.loading = true;
        state.assigning = true;
        state.error = null;
      })
      .addCase(fetchFaqAssignments.fulfilled, (state, action) => {
        // state.loading = false;
        state.assigning = false;
        state.faqAssignments = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchFaqAssignments.rejected, (state, action) => {
        // state.loading = false;
        state.assigning = false;
        state.error = action.payload;
        state.faqAssignments = [];
      })

      // Delete FAQ - uses loading
      .addCase(deleteFaq.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteFaq.fulfilled, (state, action) => {
        state.loading = false;
        state.allFaqsList = state.allFaqsList.filter(faq => faq.id !== action.payload);
      })
      .addCase(deleteFaq.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { 
  clearError, 
  clearFaqAssignments 
} = faqSlice.actions;

export const selectAllFaqsList = (state) => state.faq.allFaqsList;
export const selectFaqAssignments = (state) => state.faq.faqAssignments;
export const selectFaqLoading = (state) => state.faq.loading;
export const selectFaqSubmitting = (state) => state.faq.submitting;
export const selectFaqAssigning = (state) => state.faq.assigning;
export const selectFaqError = (state) => state.faq.error;

export default faqSlice.reducer;
